import React, { useState, useEffect, useCallback, useRef, useMemo, useContext, createContext } from 'react';
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Typography,
  Space,
  Card,
  Divider,
  Tag,
  Modal,
  Form,
  message,
  Tooltip,
  Row,
  Col,
  Dropdown,
  Menu,
  Checkbox,
  Popover,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ShoppingOutlined,
  ToolOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SearchOutlined,
  SaveOutlined,
  StarOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseOutlined,
  RobotOutlined,
  UpOutlined,
  DownOutlined,
  CameraOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { EstimateLineItem, Part, LineItemTemplate, PartSavedTemplate, ShopSettings, Vehicle } from '../types';
import { shopSettingsApi, templateApi, laborRateApi, partMarkupApi } from '../services/api';
import { LaborRatePicker } from './LaborRates/LaborRatePicker';
import { defaultNamedRate, seedHourlyRate } from './LaborRates/newLineRate';
import { WatchedMarkupPicker } from './PartsMarkup/MarkupPicker';
import { RateDiffPrompt } from './LaborRates/RateDiffPrompt';
import { buildLineTemplate, buildPartTemplate } from './Templates/templatePayloads';
import { TemplatePicker } from './Templates/TemplatePicker';
import { templateToLine, templateToPart, resolveLineRate } from './Templates/applyTemplate';
import { logEvent, newEventId } from '../services/eventLogger';
import LineItemProcedureEditor from './LineItemProcedureEditor';
import ShopNotesPanel from './Shared/ShopNotesPanel';
import { addSuggestedPart, removeSuggestedPart } from './Shared/suggestedPartEdits';
import { formatCurrency } from '../utils/formatCurrency';
import LineItemPhotosControl from './Shared/LineItemPhotosControl';
import { useCapability, useIsOwner } from './auth/RoleGuard';
import { useBreakpointDown } from '../constants/breakpoints';
import { priceFromCost, markupFromPrice } from '../utils/partMarkup';
import { computeDiscount, DiscountMode } from '../utils/discountMath';
import DiscountToggle from './Shared/DiscountToggle';
import DiscountStack from './Shared/DiscountStack';

const { Text, Title } = Typography;
const { TextArea } = Input;


// ─── Edit Row Context ────────────────────────────────────────────────────────
// Provides volatile data to the module-level CustomRow component so its
// identity never changes between renders (preventing Ant Design Table from
// unmounting/remounting rows and killing input focus).
interface EditRowContextValue {
  items: EstimateLineItem[];
  editingKey: string;
  form: any;
  isMobile: boolean;
  disabled: boolean;
  discountType: 'percentage' | 'amount' | 'final_price';
  setDiscountType: (t: 'percentage' | 'amount' | 'final_price') => void;
  defaultLaborRate: number;
  canEditPrices: boolean;
  /**
   * May this user type a labor rate that is not one of the shop's configured
   * rates? Owners always can; anyone else needs the `custom_labor_rate`
   * capability. Without it the picker is the only way to set a rate, which is
   * what keeps quoted labor on the shop's published numbers.
   */
  canSetCustomLaborRate: boolean;
  save: (id: number | string) => Promise<void>;
  cancel: () => void;
  recalculateLineTotalOnChange: () => void;
  columns: any[];
  /** True when rendered inside the stacked card layout (<1280) — drives the
   *  edit form to a single-column grid so the labor-discount control isn't crushed. */
  narrow: boolean;
}

/** Gross margin from revenue and cost-of-goods. Parts carry a cost; labor does
 *  not, so a line's margin is its revenue minus the cost of its parts. */
function marginInfo(revenue: number, cost: number): { amount: number; pct: number } {
  const amount = revenue - cost;
  const pct = revenue > 0 ? (amount / revenue) * 100 : 0;
  return { amount, pct };
}

/** Per-unit part cost: prefer authoritative cents, fall back to legacy dollars. */
function partUnitCost(part: Part): number {
  return (part as any).cost_cents != null ? (part as any).cost_cents / 100 : (part.cost || 0);
}

/** Labor discount control — thin Form-aware wrapper around the shared DiscountToggle
 *  (frontend/src/components/Shared/DiscountToggle.tsx) so the estimate's labor
 *  discount UI + math come from the same module the WO editor consumes. Values
 *  live in `form` under labor_discount_percentage/_amount/_final_price (final_price
 *  is UI-only — the estimate schema has no such column, see save()). Uses
 *  Form.useWatch (not getFieldValue) so it re-renders live as the user types —
 *  same fix as WatchedMarkupPicker in PartsMarkup/MarkupPicker.tsx. */
function LaborDiscountControl({
  form, base, mode, setMode, size, onAfterChange,
}: {
  form: any;
  base: number;
  mode: DiscountMode;
  setMode: (t: DiscountMode) => void;
  size?: 'small' | 'middle';
  onAfterChange: () => void;
}): React.ReactElement {
  const percentage = Form.useWatch('labor_discount_percentage', form);
  const amount = Form.useWatch('labor_discount_amount', form);
  const finalPrice = Form.useWatch('labor_discount_final_price', form);
  return (
    <DiscountToggle
      base={base}
      size={size}
      value={{ mode, percentage, amount, finalPrice }}
      onChange={(v) => {
        setMode(v.mode);
        form.setFieldsValue({
          labor_discount_percentage: v.percentage,
          labor_discount_amount: v.amount,
          labor_discount_final_price: v.finalPrice,
        });
        onAfterChange();
      }}
    />
  );
}

/** Part discount control — same wrapper pattern as LaborDiscountControl, but for
 *  the part editor's `partForm` (fields: discount_percentage/_amount/_final_price)
 *  and rounded to cents (parts persist rounded money; labor does not). `base` is
 *  live unit_price × quantity so the shared Net preview tracks in-progress edits. */
function PartDiscountControl({
  partForm, mode, setMode, size,
}: {
  partForm: any;
  mode: DiscountMode;
  setMode: (t: DiscountMode) => void;
  size?: 'small' | 'middle';
}): React.ReactElement {
  const unitPrice = Form.useWatch('unit_price', partForm);
  const quantity = Form.useWatch('quantity', partForm);
  const percentage = Form.useWatch('discount_percentage', partForm);
  const amount = Form.useWatch('discount_amount', partForm);
  const finalPrice = Form.useWatch('discount_final_price', partForm);
  const base = (unitPrice || 0) * (quantity || 0);
  return (
    <DiscountToggle
      base={base}
      round
      size={size}
      value={{ mode, percentage, amount, finalPrice }}
      onChange={(v) => {
        setMode(v.mode);
        partForm.setFieldsValue({
          discount_percentage: v.percentage,
          discount_amount: v.amount,
          discount_final_price: v.finalPrice,
        });
      }}
    />
  );
}

/** ONE consistent discount presentation used everywhere (line labor, line parts
 *  combined, and each part): original (strikethrough) → −$amount (−pct%) → final.
 *  When there's no discount it shows just the final price. Delegates to the
 *  shared DiscountStack component so estimate + WO render identically. */
function discountStack(original: number, final: number, opts?: { explicitPct?: number; strong?: boolean }): React.ReactElement {
  return <DiscountStack original={original} net={final} percentage={opts?.explicitPct} strong={opts?.strong} />;
}

/** A small "Margin $X (Y%)" popover trigger — owner-only callers gate visibility. */
function MarginPopover({ revenue, cost, label = 'Margin' }: { revenue: number; cost: number; label?: string }): React.ReactElement {
  const { amount, pct } = marginInfo(revenue, cost);
  const color = pct >= 40 ? '#10b981' : pct >= 20 ? '#f59e0b' : '#ef4444';
  return (
    <Popover
      trigger={['hover', 'click']}
      content={
        <div style={{ fontSize: 12, lineHeight: 1.6, minWidth: 140 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><span style={{ color: '#6b7280' }}>Revenue</span><span>{formatCurrency(revenue)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><span style={{ color: '#6b7280' }}>Cost</span><span>{formatCurrency(cost)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderTop: '1px solid #f0f0f0', marginTop: 4, paddingTop: 4, fontWeight: 600 }}><span>Margin</span><span style={{ color }}>{formatCurrency(amount)} ({pct.toFixed(0)}%)</span></div>
        </div>
      }
    >
      <span style={{ fontSize: 11, color, cursor: 'pointer', borderBottom: `1px dashed ${color}`, whiteSpace: 'nowrap' }}>
        {label} {pct.toFixed(0)}%
      </span>
    </Popover>
  );
}

const EditRowContext = createContext<EditRowContextValue>(null!);

// ─── Static data ─────────────────────────────────────────────────────────────
const predefinedCategories = [
  'Diagnostic', 'Maintenance', 'Brakes', 'Electrical', 'Safety', 'Transmission',
  'Engine', 'Cooling', 'Suspension', 'Exhaust', 'Body Work', 'Interior', 'Custom'
];

// ─── Line-item edit form content ─────────────────────────────────────────────
// Shared between CustomRow (table mode, wraps in <tr><td>) and card mode
// (wraps in <Card>). Accepts the record plus the full context so it can be
// called from module scope.
function renderLineItemEditContent(record: EstimateLineItem, ctx: EditRowContextValue): React.ReactElement {
  const { form, isMobile, narrow, discountType, setDiscountType, defaultLaborRate, canEditPrices, canSetCustomLaborRate, save, cancel, recalculateLineTotalOnChange } = ctx;
  const recordKey = (record.id || 'new').toString();
  const baseLaborCost = (record.labor_hours || 0) * (record.hourly_rate || 0);
  const laborDiscountAmount = record.labor_discount_amount ||
    (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
  const discountedLaborCost = baseLaborCost - laborDiscountAmount;
  const partsCost = (record.parts || []).reduce((sum: number, part: Part) => sum + (part.discounted_price ?? part.total_price), 0);
  const totalPrice = discountedLaborCost + partsCost;

  return (
    <div style={{
      background: '#fff',
      border: '2px solid #3b82f6',
      borderRadius: 12,
      margin: isMobile ? 8 : 12,
      padding: isMobile ? 16 : 24,
      boxShadow: '0 4px 12px rgba(59,130,246,0.08)',
    }}>
            {/* Row 1: Category + Description */}
            <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', gap: narrow ? 12 : 16, marginBottom: 16 }}>
              <div style={{ width: narrow ? '100%' : 200, flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Category</div>
                <Form.Item name="category" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
                  <Select placeholder="Select category" size="large" style={{ width: '100%' }}>
                    {predefinedCategories.map(cat => (
                      <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Service Description</div>
                <Form.Item name="description" style={{ margin: 0 }} rules={[{ required: true, message: 'Required' }]}>
                  <Input placeholder="e.g. Brake pad replacement, front and rear" size="large" />
                </Form.Item>
              </div>
            </div>

            {/* Row 2: Labor fields — responsive grid. In the narrow card layout the
                labor-discount control gets its own full-width row so it isn't crushed. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: narrow ? '1fr 1fr' : '80px 1fr 1fr 1fr',
              gap: narrow ? 12 : 16,
              marginBottom: 16,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Qty</div>
                <Form.Item name="quantity" style={{ margin: 0 }} rules={[{ required: true, message: 'Req' }]}>
                  <InputNumber min={1} style={{ width: '100%' }} size="large" controls={false} />
                </Form.Item>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Labor Hours</div>
                <Form.Item name="labor_hours" style={{ margin: 0 }}>
                  <InputNumber
                    min={0} step={0.1} precision={1} style={{ width: '100%' }}
                    placeholder="0.0" size="large" controls={false} suffix="h"
                    data-testid="labor-hours-input"
                    onBlur={() => recalculateLineTotalOnChange()}
                  />
                </Form.Item>
              </div>
              <div style={{ gridColumn: narrow ? '1 / -1' : undefined }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Hourly Rate</div>
                <Form.Item name="labor_rate_id" hidden><InputNumber /></Form.Item>
                {/* Rate preset picker + editable $ override. Inline on narrow
                    viewports (cell spans the full row); stacked in the desktop
                    grid where the column is too tight for both side by side. */}
                <div style={{
                  display: 'flex',
                  flexDirection: narrow ? 'row' : 'column',
                  gap: narrow ? 8 : 4,
                }}>
                  <LaborRatePicker
                    valueId={form.getFieldValue('labor_rate_id')}
                    onPick={(r) => {
                      form.setFieldsValue({ labor_rate_id: r.id, hourly_rate: r.hourly_rate });
                      recalculateLineTotalOnChange();
                    }}
                    disabled={!canEditPrices}
                    size="large"
                    style={narrow ? { flex: 1, minWidth: 0 } : { width: '100%' }}
                  />
                  <Form.Item name="hourly_rate" style={{ margin: 0, width: narrow ? 140 : '100%', flexShrink: 0 }}>
                    <InputNumber
                      min={0} step={5} style={{ width: '100%' }}
                      placeholder={`$${(defaultLaborRate || 0).toFixed(2)}`} size="large" controls={false} prefix="$"
                      // Locked to the shop's configured rates unless this user is
                      // an owner or carries `custom_labor_rate`. The picker above
                      // stays live either way, so a rate can always be CHOSEN —
                      // only typing an off-menu number is gated.
                      disabled={!canEditPrices || !canSetCustomLaborRate}
                      title={
                        !canSetCustomLaborRate
                          ? 'Choose one of the shop’s labor rates — a custom rate needs owner approval'
                          : undefined
                      }
                      onBlur={() => recalculateLineTotalOnChange()}
                    />
                  </Form.Item>
                </div>
              </div>
              <div style={{ gridColumn: narrow ? '1 / -1' : undefined }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Labor Discount
                </div>
                {/* Hidden field carriers — the shared DiscountToggle drives these via
                    form.setFieldsValue so save()/recalculateLineTotalOnChange (which
                    read form.getFieldsValue/validateFields) see identical values to
                    the old per-mode Form.Items. */}
                <Form.Item name="labor_discount_percentage" hidden><InputNumber /></Form.Item>
                <Form.Item name="labor_discount_amount" hidden><InputNumber /></Form.Item>
                <Form.Item name="labor_discount_final_price" hidden><InputNumber /></Form.Item>
                <LaborDiscountControl
                  form={form}
                  base={baseLaborCost}
                  mode={discountType}
                  setMode={setDiscountType}
                  size="middle"
                  onAfterChange={recalculateLineTotalOnChange}
                />
              </div>
            </div>

            {/* Row 3: Notes (optional) */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></div>
              <Form.Item name="notes" style={{ margin: 0 }}>
                <Input.TextArea placeholder="Internal notes about this service..." rows={2} size="large" />
              </Form.Item>
            </div>

            {/* Migration 192 — mark the diagnosis here, where the work is written.
                The flag carries onto the work-order line at conversion, which is
                what the diagnosis credit is computed from, and it books the visit
                as a diagnosis instead of a repair. Set after the fact, it was
                routinely forgotten and every credit came out $0. */}
            <div style={{ marginBottom: 16 }}>
              <Form.Item name="is_diagnosis" valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox data-testid="line-is-diagnosis">
                  This is the diagnosis fee
                  <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>
                    (credited against the repair if they go ahead)
                  </span>
                </Checkbox>
              </Form.Item>
            </div>

            {/* Footer: Total + Actions */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderTop: '1px solid #e5e7eb', paddingTop: 16,
            }}>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Line Total</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>
                  {formatCurrency(totalPrice)}
                </div>
                {laborDiscountAmount > 0 && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    <span style={{ textDecoration: 'line-through' }}>{formatCurrency(baseLaborCost)}</span>
                    {' '}<span style={{ color: '#10b981' }}>saved {formatCurrency(laborDiscountAmount)}</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button size="large" onClick={cancel}>
                  Cancel
                </Button>
                <Button type="primary" size="large" onClick={() => save(recordKey)} style={{ minWidth: 100 }}>
                  Save
                </Button>
              </div>
            </div>
          </div>
  );
}

// ─── Module-scope CustomRow ──────────────────────────────────────────────────
// Extracted to module scope so its identity is stable across parent re-renders.
// Reads volatile data via EditRowContext instead of closure capture.
const CustomRow = (props: any) => {
  const ctx = useContext(EditRowContext);
  if (!ctx) return <tr {...props} />;
  const { items, editingKey, columns } = ctx;

  const record = items.find((item: EstimateLineItem) => (item.id || 'new').toString() === props['data-row-key']);
  const editable = record && (record.id || 'new').toString() === editingKey;

  if (editable) {
    return (
      <tr {...props} className="editing-row">
        <td colSpan={columns.length} style={{ padding: 0 }}>
          {renderLineItemEditContent(record, ctx)}
        </td>
      </tr>
    );
  }

  return <tr {...props} />;
};

interface ServiceItemsTableProps {
  items: EstimateLineItem[];
  onChange: (items: EstimateLineItem[]) => void;
  disabled?: boolean;
  taxRate?: number;
  vehicle?: Vehicle;
}

// ─── Internal Job Steps Panel ─────────────────────────────────────────────────

interface InternalJobStepsPanelProps {
  steps: Array<{ step: string; hours: number }>;
  lineItem: EstimateLineItem;
  itemIndex: number;
  onLaborHoursChange?: (index: number, newTotal: number) => void;
  readOnly?: boolean;
}

const InternalJobStepsPanel: React.FC<InternalJobStepsPanelProps> = ({
  steps,
  lineItem,
  itemIndex,
  onLaborHoursChange,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // collapsed by default
  const [localSteps, setLocalSteps] = useState<Array<{ step: string; hours: number }>>(steps);

  useEffect(() => {
    setLocalSteps(steps);
  }, [steps]);

  const totalHours = localSteps.reduce((sum, s) => sum + (s.hours || 0), 0);

  const handleHoursChange = (idx: number, newHours: number) => {
    const updated = localSteps.map((s, i) => i === idx ? { ...s, hours: newHours } : s);
    setLocalSteps(updated);
    const newTotal = updated.reduce((sum, s) => sum + (s.hours || 0), 0);
    if (onLaborHoursChange) {
      onLaborHoursChange(itemIndex, Math.round(newTotal * 100) / 100);
    }
  };

  if (localSteps.length === 0) return null;

  return (
    <div
      style={{
        margin: '8px 0 12px 0',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#f8fafc',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '7px 12px',
          background: '#f1f5f9',
          cursor: 'pointer',
          borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#64748b', fontSize: 12, fontWeight: 500 }}>
            {isExpanded ? '▾' : '▸'}
          </span>
          <span style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
            Job Steps
          </span>
          <span
            style={{
              background: '#475569',
              color: '#fff',
              borderRadius: 10,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {localSteps.length} steps
          </span>
          <span
            style={{
              background: '#64748b',
              color: '#fff',
              borderRadius: 10,
              padding: '1px 8px',
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {totalHours.toFixed(1)}h total
          </span>
        </div>
        <Button
          type="text"
          size="small"
          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
          style={{ color: '#64748b' }}
        />
      </div>

      {/* Steps list */}
      {isExpanded && (
        <div style={{ padding: '8px 14px 12px 14px' }}>
          {/* Column headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 90px',
              gap: 8,
              padding: '4px 0',
              marginBottom: 4,
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Step Description
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
              Hours
            </span>
          </div>

          {localSteps.map((s, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 90px',
                gap: 8,
                padding: '5px 0',
                borderBottom: idx < localSteps.length - 1 ? '1px solid #f1f5f9' : 'none',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span
                  style={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#94a3b8',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {idx + 1}
                </span>
                <span style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>
                  {s.step}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                {readOnly ? (
                  <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                    {s.hours.toFixed(1)}h
                  </span>
                ) : (
                  <InputNumber
                    size="small"
                    min={0}
                    max={40}
                    step={0.1}
                    precision={1}
                    value={s.hours}
                    onChange={(val) => handleHoursChange(idx, val ?? 0)}
                    style={{ width: 70 }}
                    suffix="h"
                  />
                )}
              </div>
            </div>
          ))}

          {/* Summary row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 90px',
              gap: 8,
              padding: '6px 0 2px 0',
              marginTop: 4,
              borderTop: '2px solid #cbd5e1',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
              Total Estimated Hours
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', textAlign: 'right' }}>
              {totalHours.toFixed(1)}h
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Shared part edit form fields ────────────────────────────────────────────
// Called from BOTH the stacked card edit path (stackCards branch) and the
// desktop parts table (via colSpan on the Part # column), so future field
// changes only need to be made once.
interface RenderPartEditFieldsParams {
  partDiscountType: 'percentage' | 'amount' | 'final_price';
  setPartDiscountType: (t: 'percentage' | 'amount' | 'final_price') => void;
  partForm: any;
  canEditPrices: boolean;
  cancelPartEdit: () => void;
  savePartEdit: (id: string | number) => void;
  partKey: string;
}

function renderPartEditFields({
  partDiscountType,
  setPartDiscountType,
  partForm,
  canEditPrices,
  cancelPartEdit,
  savePartEdit,
  partKey,
}: RenderPartEditFieldsParams): React.ReactElement {
  return (
    <div style={{ border: '2px solid #3b82f6', borderRadius: 8, padding: 12, marginBottom: 8, background: '#fff' }}>
      {/* Part # + Qty */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Part #</div>
          <Form.Item name="part_number" style={{ margin: 0 }} rules={[{ required: true, message: 'Part number is required' }]}>
            <Input placeholder="P12345" size="small" />
          </Form.Item>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Qty</div>
          <Form.Item name="quantity" style={{ margin: 0 }} rules={[{ required: true, message: 'Quantity is required' }]}>
            <InputNumber min={1} style={{ width: '100%' }} size="small" controls={false} />
          </Form.Item>
        </div>
      </div>
      {/* Description */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Description</div>
        <Form.Item name="description" style={{ margin: 0 }} rules={[{ required: true, message: 'Description is required' }]}>
          <Input placeholder="Part description" size="small" />
        </Form.Item>
      </div>
      {/* Cost | Markup */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Cost</div>
          <Form.Item name="cost" style={{ margin: 0 }}>
            <InputNumber
              min={0} step={0.01} style={{ width: '100%' }} size="small" controls={false} placeholder="cost"
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              onChange={(v) => {
                const pct = partForm.getFieldValue('markup_percentage');
                if (v != null && pct != null) partForm.setFieldValue('unit_price', priceFromCost(Number(v), Number(pct)));
              }}
            />
          </Form.Item>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Markup</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Form.Item name="markup_preset_id" hidden><InputNumber /></Form.Item>
            <WatchedMarkupPicker
              form={partForm}
              onPick={(p) => {
                const cost = partForm.getFieldValue('cost') || 0;
                partForm.setFieldsValue({ markup_preset_id: p.id, markup_percentage: p.markup_percentage });
                if (cost > 0) partForm.setFieldValue('unit_price', priceFromCost(cost, p.markup_percentage));
              }}
              size="small"
              style={{ flex: 1, minWidth: 0 }}
            />
            <Form.Item name="markup_percentage" style={{ margin: 0, width: 84, flexShrink: 0 }}>
              <InputNumber
                min={0} max={1000} step={1} size="small" controls={false} suffix="%"
                style={{ width: '100%' }}
                disabled={!canEditPrices}
                onChange={(v) => {
                  const cost = partForm.getFieldValue('cost') || 0;
                  if (v != null) {
                    partForm.setFieldValue('markup_preset_id', null); // custom override
                    if (cost > 0) partForm.setFieldValue('unit_price', priceFromCost(cost, Number(v)));
                  }
                }}
              />
            </Form.Item>
          </div>
        </div>
      </div>
      {/* Unit Price */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Unit Price</div>
        <Form.Item name="unit_price" style={{ margin: 0 }} rules={[{ required: true, message: 'Price is required' }]}>
          <InputNumber
            min={0} step={0.01} style={{ width: '100%' }} size="small" controls={false}
            disabled={!canEditPrices}
            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
            onChange={(v) => {
              const cost = partForm.getFieldValue('cost') || 0;
              const pct = markupFromPrice(Number(cost), Number(v ?? 0));
              if (pct != null) partForm.setFieldsValue({ markup_percentage: pct, markup_preset_id: null });
            }}
          />
        </Form.Item>
      </div>
      {/* Discount toggle + inputs */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
          Discount
        </div>
        {/* Hidden field carriers — see LaborDiscountControl comment above; same
            pattern, driven by the shared DiscountToggle via partForm. */}
        <Form.Item name="discount_percentage" hidden><InputNumber /></Form.Item>
        <Form.Item name="discount_amount" hidden><InputNumber /></Form.Item>
        <Form.Item name="discount_final_price" hidden><InputNumber /></Form.Item>
        <PartDiscountControl
          partForm={partForm}
          mode={partDiscountType}
          setMode={setPartDiscountType}
        />
      </div>
      {/* Availability */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Availability</div>
        <Form.Item name="availability" style={{ margin: 0 }} rules={[{ required: true, message: 'Availability is required' }]}>
          <Select style={{ width: '100%' }} size="small">
            <Select.Option value="in_stock">In Stock</Select.Option>
            <Select.Option value="order_required">Order Required</Select.Option>
            <Select.Option value="backordered">Backordered</Select.Option>
            <Select.Option value="discontinued">Discontinued</Select.Option>
          </Select>
        </Form.Item>
      </div>
      {/* Save / Cancel */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button size="small" onClick={cancelPartEdit}>Cancel</Button>
        <Button type="primary" size="small" onClick={() => savePartEdit(partKey)}>Save</Button>
      </div>
    </div>
  );
}

interface PartsSectionProps {
  lineItem: EstimateLineItem;
  onChange: (lineItem: EstimateLineItem) => void;
  disabled?: boolean;
  vehicle?: Vehicle;
  stackCards?: boolean;
  partForm: any;
  editingPartKey: string;
  setEditingPartKey: (key: string) => void;
  partDiscountType: 'percentage' | 'amount' | 'final_price';
  setPartDiscountType: (t: 'percentage' | 'amount' | 'final_price') => void;
  markupPresets: { id: number; name: string; markup_percentage: number; is_default: number }[];
}

const PartsSection: React.FC<PartsSectionProps> = ({
  lineItem,
  onChange,
  disabled = false,
  vehicle,
  stackCards = false,
  partForm,
  editingPartKey,
  setEditingPartKey,
  partDiscountType,
  setPartDiscountType,
  markupPresets,
}) => {
  const canEditPrices = useCapability('edit_prices');
  // Part COST (what the shop pays) is margin-sensitive → owner-only.
  const canSeeCost = useIsOwner();
  const [savedPartTemplateModalVisible, setSavedPartTemplateModalVisible] = useState(false);
  const [deletePartModalVisible, setDeletePartModalVisible] = useState(false);
  const [partToDelete, setPartToDelete] = useState<string>('');
  const [savePartTemplateModalVisible, setSavePartTemplateModalVisible] = useState(false);
  const [partToSave, setPartToSave] = useState<Part | null>(null);
  const [savePartTemplateForm] = Form.useForm();

  // Check if last part is empty (newly added) and auto-edit it
  useEffect(() => {
    if (lineItem.parts && lineItem.parts.length > 0) {
      const lastPart = lineItem.parts[lineItem.parts.length - 1];
      // If the last part has no part_number and no description, it's a newly added empty part
      if (!lastPart.part_number && !lastPart.description && !editingPartKey) {
        console.log('Auto-editing new empty part:', lastPart);
        editPart(lastPart);
      }
    }
  }, [lineItem.parts?.length]); // Only run when parts array length changes

  // Composite key: scopes the edit to a specific (line-item, part) pair so that
  // shared partForm + editingPartKey lifted to ServiceItemsTable never confuse
  // parts from different line items.
  const buildPartKey = (part: Part) => `${lineItem.id ?? 'new-li'}::${part.id ?? part.part_number ?? 'new'}`;

  const isEditingPart = (part: Part) => buildPartKey(part) === editingPartKey;

  const editPart = (part: Part) => {
    const newKey = buildPartKey(part);
    // Guard: if a DIFFERENT part is already in-edit, refuse to silently discard it.
    if (editingPartKey && editingPartKey !== newKey) {
      message.warning("Finish or cancel the part you're editing first.");
      return;
    }
    partForm.setFieldsValue({
      part_number: part.part_number,
      description: part.description,
      quantity: part.quantity,
      unit_price: part.unit_price,
      cost: part.cost ?? undefined,
      discount_percentage: part.discount_percentage,
      discount_amount: part.discount_amount,
      availability: part.availability,
      supplier: part.supplier,
      notes: part.notes,
      markup_preset_id: part.markup_preset_id ?? null,
      markup_percentage: part.markup_percentage ?? null,
    });
    setEditingPartKey(newKey);
  };

  const cancelPartEdit = () => {
    // If the part being edited has empty part_number and description, it's a new unsaved part - delete it
    const editingPart = (lineItem.parts || []).find(part => buildPartKey(part) === editingPartKey);

    if (editingPart && !editingPart.part_number && !editingPart.description) {
      // This is a new part that was never saved - delete it
      const filteredParts = (lineItem.parts || []).filter(part => buildPartKey(part) !== editingPartKey);
      const laborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
      const partsCost = filteredParts.reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);
      const totalServicePrice = laborCost + partsCost;

      onChange({
        ...lineItem,
        parts: filteredParts,
        total_price: totalServicePrice,
      });
    }
    // Otherwise it's an existing part - just cancel the edit (revert happens automatically)

    setEditingPartKey('');
    partForm.resetFields();
  };

  const savePartEdit = async (id: number | string) => {
    try {
      const values = await partForm.validateFields();
      const updatedParts = [...(lineItem.parts || [])];
      const partIndex = updatedParts.findIndex(part => (part.id || 'new').toString() === id.toString());

      if (partIndex > -1) {
        const part = updatedParts[partIndex];
        const unitPrice = values.unit_price || 0;
        const quantity = values.quantity || 1;
        const baseTotal = unitPrice * quantity;

        const discountPercentage = values.discount_percentage || 0;

        // Calculate final discounted price with rounding (parts persist rounded money)
        const { discountAmount: finalDiscountAmount, net: discountedPrice } = computeDiscount({
          base: baseTotal,
          mode: values.discount_final_price > 0 ? 'final_price'
              : values.discount_amount ? 'amount' : 'percentage',
          percentage: discountPercentage,
          amount: values.discount_amount,
          finalPrice: values.discount_final_price,
          round: true,
        });

        updatedParts[partIndex] = {
          ...part,
          ...values,
          markup_preset_id: partForm.getFieldValue('markup_preset_id') ?? null,
          markup_percentage: partForm.getFieldValue('markup_percentage') ?? null,
          total_price: baseTotal,
          discount_amount: finalDiscountAmount,
          discount_percentage: discountPercentage,
          discounted_price: discountedPrice,
        };
      }

      const baseLaborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
      const laborDiscountAmt = lineItem.labor_discount_amount ||
        (baseLaborCost * (lineItem.labor_discount_percentage || 0) / 100);
      const laborCost = baseLaborCost - laborDiscountAmt;
      const partsCost = updatedParts.reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);
      const totalServicePrice = Number((laborCost + partsCost).toFixed(2));

      onChange({
        ...lineItem,
        parts: updatedParts,
        total_price: totalServicePrice,
      });

      setEditingPartKey('');
      partForm.resetFields();
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const addNewPart = () => {
    const defaultPreset = markupPresets.find(p => p.is_default);
    const newPart: Part = {
      id: Date.now(),
      line_item_id: lineItem.id,
      part_number: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      discount_percentage: 0,
      discount_amount: 0,
      discounted_price: 0,
      availability: 'in_stock',
      markup_preset_id: defaultPreset ? defaultPreset.id : null,
      markup_percentage: defaultPreset ? defaultPreset.markup_percentage : null,
    };
    const updatedParts = [...(lineItem.parts || []), newPart];

    onChange({
      ...lineItem,
      parts: updatedParts,
    });
    // The useEffect will automatically detect the new empty part and edit it
  };

  const addFromSavedPartTemplate = (template: any /* Template */) => {
    const newPart = templateToPart(template, lineItem.id, markupPresets);
    const updatedParts = [...(lineItem.parts || []), newPart];
    const laborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
    const partsCost = updatedParts.reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);
    onChange({ ...lineItem, parts: updatedParts, total_price: laborCost + partsCost });
    templateApi.use(template.id).catch(() => {});
    setSavedPartTemplateModalVisible(false);
    message.success(`Added ${template.name} to parts list`);
  };

  const savePartAsTemplate = (part: Part) => {
    setPartToSave(part);
    savePartTemplateForm.setFieldsValue({
      name: `${part.description} - Part Template`,
      isGeneral: !vehicle, // Default to general if no vehicle, vehicle-specific if vehicle exists
      vehicleYears: vehicle ? [vehicle.year] : []
    });
    setSavePartTemplateModalVisible(true);
  };

  // Generate year options from 1990 to current year + 2
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear + 2 - 1990 + 1 }, (_, i) => 1990 + i);

  const handleSavePartTemplate = async () => {
    try {
      if (!partToSave) return;

      const values = await savePartTemplateForm.validateFields();

      const template: Omit<PartSavedTemplate, 'id'> = {
        name: values.name,
        part_number: partToSave.part_number,
        description: partToSave.description,
        unit_price: partToSave.unit_price,
        discount_percentage: partToSave.discount_percentage,
        discount_amount: partToSave.discount_amount,
        availability: partToSave.availability,
        supplier: partToSave.supplier,
        notes: partToSave.notes,
      };

      // Add vehicle information if not marked as general
      if (!values.isGeneral && vehicle && values.vehicleYears && values.vehicleYears.length > 0) {
        template.vehicle_years = values.vehicleYears.join(',');
        template.vehicle_make = vehicle.make;
        template.vehicle_model = vehicle.model;
      }

      await templateApi.create(buildPartTemplate({ name: values.name, part: partToSave, vehicle, vehicleYears: values.isGeneral ? undefined : values.vehicleYears }));
      message.success(`Part template "${values.name}" saved successfully!`);
      setSavePartTemplateModalVisible(false);
      setPartToSave(null);
      savePartTemplateForm.resetFields();
    } catch (error) {
      console.error('Error saving part template:', error);
      message.error('Failed to save part template');
    }
  };

  const deletePart = (id: number | string) => {
    console.log('Deleting part with id:', id);
    console.log('Current parts:', lineItem.parts);
    const filteredParts = (lineItem.parts || []).filter(part => (part.id || 'new').toString() !== id.toString());
    console.log('Filtered parts:', filteredParts);
    const laborCost = (lineItem.labor_hours || 0) * (lineItem.hourly_rate || 0);
    const partsCost = filteredParts.reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);
    const totalServicePrice = laborCost + partsCost;

    onChange({
      ...lineItem,
      parts: filteredParts,
      total_price: totalServicePrice,
    });
  };

  const handleConfirmDeletePart = () => {
    console.log('User confirmed delete');
    deletePart(partToDelete);
    setDeletePartModalVisible(false);
    setPartToDelete('');
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'green';
      case 'order_required': return 'orange';
      case 'backordered': return 'red';
      case 'discontinued': return 'default';
      default: return 'default';
    }
  };

  const getAvailabilityIcon = (availability: string) => {
    switch (availability) {
      case 'in_stock': return <CheckCircleOutlined />;
      case 'order_required': return <ClockCircleOutlined />;
      case 'backordered': return <ExclamationCircleOutlined />;
      case 'discontinued': return <StopOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'in_stock': return 'In Stock';
      case 'order_required': return 'Order Required';
      case 'backordered': return 'Backordered';
      case 'discontinued': return 'Discontinued';
      default: return availability;
    }
  };

  // 8 base columns (Part#, Description, Qty, Unit Price, Discount, Total, Availability, Actions)
  // + 1 if owner can see cost — used for colSpan when editing.
  const totalPartCols = canSeeCost ? 9 : 8;

  const partColumns = [
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      width: 90,
      onCell: (record: Part) => ({
        colSpan: isEditingPart(record) ? totalPartCols : 1,
      }),
      render: (partNumber: string, record: Part) => {
        if (isEditingPart(record)) {
          // Span all columns: render the shared edit form card.
          return renderPartEditFields({
            partDiscountType, setPartDiscountType, partForm,
            canEditPrices, cancelPartEdit, savePartEdit,
            partKey: (record.id || 'new').toString(),
          });
        }
        return (
          <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {partNumber || '—'}
          </Text>
        );
      },
    },
    {
      title: 'Part Description',
      dataIndex: 'description',
      width: 130,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (description: string, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0
        return (
          <div>
            <div style={{ fontWeight: '500', fontSize: '12px', wordWrap: 'break-word', lineHeight: '1.3' }}>
              {description}
              {Boolean(record.is_ai_suggested) && (
                <Tag
                  color="geekblue"
                  style={{ marginLeft: '4px', fontSize: '9px', padding: '0 3px', lineHeight: '16px', verticalAlign: 'middle' }}
                >
                  AI ✦
                </Tag>
              )}
              {Boolean(record.is_optional) && (
                <Tag
                  color="orange"
                  style={{ marginLeft: '2px', fontSize: '9px', padding: '0 3px', lineHeight: '16px', verticalAlign: 'middle' }}
                >
                  Optional
                </Tag>
              )}
            </div>
            {record.supplier && (
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '2px' }}>
                {record.supplier}
              </Text>
            )}
            {record.is_ai_suggested && typeof record.ai_confidence === 'number' && (
              <Text
                type="secondary"
                style={{
                  fontSize: '10px',
                  display: 'block',
                  marginTop: '1px',
                  color: record.ai_confidence >= 0.7 ? '#10b981' : record.ai_confidence >= 0.5 ? '#f59e0b' : '#ef4444',
                }}
              >
                {Math.round(record.ai_confidence * 100)}% confidence
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 50,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (quantity: number, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0
        return quantity;
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      width: 70,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (price: number, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0
        // AI-suggested parts with no price set show a placeholder instead of $0.00
        if (record.is_ai_suggested && (price === 0 || price == null)) {
          return (
            <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
              — set price
            </Text>
          );
        }
        return formatCurrency(price);
      },
    },
    // Owner-only: per-unit COST (what the shop pays) — drives margin/profit.
    ...(canSeeCost ? [{
      title: 'Cost',
      dataIndex: 'cost',
      width: 70,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (cost: number | null | undefined, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0
        return cost != null
          ? formatCurrency(cost)
          : <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>— set cost</Text>;
      },
    }] : []),
    {
      title: 'Discount',
      dataIndex: 'discount',
      width: 130,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (_: any, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0

        const hasDiscount = record.discount_percentage || record.discount_amount;
        if (!hasDiscount) return '—';

        return (
          <div style={{ fontSize: '11px' }}>
            {record.discount_percentage && (
              <div style={{ color: '#ef4444' }}>{record.discount_percentage.toFixed(1)}%</div>
            )}
            {Boolean(record.discount_amount) && (
              <div style={{ color: '#ef4444' }}>-{formatCurrency(record.discount_amount).substring(1)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      width: 70,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (_: any, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0
        const baseTotal = record.unit_price * record.quantity;
        const discountAmount = record.discount_amount || 0;
        const finalPrice = record.discounted_price ?? (baseTotal - discountAmount);

        if (discountAmount > 0) {
          return (
            <div style={{ fontSize: '11px' }}>
              <div style={{ textDecoration: 'line-through', color: '#999' }}>
                {formatCurrency(baseTotal)}
              </div>
              <div style={{ color: '#10b981', fontWeight: '600' }}>
                {formatCurrency(finalPrice)}
              </div>
            </div>
          );
        }

        return (
          <div style={{ color: '#10b981', fontWeight: '600', fontSize: '12px' }}>
            {formatCurrency(finalPrice)}
          </div>
        );
      },
    },
    {
      title: 'Availability',
      dataIndex: 'availability',
      width: 90,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (availability: string, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0
        return (
          <Tag
            icon={getAvailabilityIcon(availability)}
            color={getAvailabilityColor(availability)}
            style={{ fontSize: '9px' }}
          >
            {availability === 'in_stock' ? 'In Stock' :
             availability === 'order_required' ? 'Order Req.' :
             availability === 'backordered' ? 'Backorder' :
             availability === 'discontinued' ? 'Disc.' : availability}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      width: 50,
      onCell: (record: Part) => ({ colSpan: isEditingPart(record) ? 0 : 1 }),
      render: (text: any, record: Part) => {
        if (isEditingPart(record)) return null; // hidden by onCell colSpan: 0 — Save/Cancel in renderPartEditFields
        const recordKey = (record.id || 'new').toString();

        const handleMenuClick = (e: { key: string }) => {
          console.log('Part menu clicked:', e.key, 'for record:', recordKey);

          switch (e.key) {
            case 'edit':
              editPart(record);
              break;
            case 'save-template':
              savePartAsTemplate(record);
              break;
            case 'delete':
              console.log('Delete menu item clicked');
              cancelPartEdit();
              setPartToDelete(recordKey);
              setDeletePartModalVisible(true);
              break;
          }
        };

        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
          },
          {
            key: 'save-template',
            icon: <SaveOutlined />,
            label: 'Save as Template',
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={['click']}
            disabled={disabled}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              disabled={disabled}
            />
          </Dropdown>
        );
      },
    },
  ];

  // ─── Stacked card layout for < 1024 px (no h-scrolling table) ──────────────
  const partSaveModal = (
      <Modal
        title="Save Part as Template"
        open={savePartTemplateModalVisible}
        onOk={handleSavePartTemplate}
        onCancel={() => {
          setSavePartTemplateModalVisible(false);
          setPartToSave(null);
          savePartTemplateForm.resetFields();
        }}
        okText="Save Template"
        cancelText="Cancel"
        centered
        width={500}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Save this part as a reusable template{vehicle ? ` for ${vehicle.year} ${vehicle.make} ${vehicle.model}` : ''}. You can use it later in any estimate.
          </Text>
        </div>
        {partToSave && (
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '1px solid #e5e7eb'
          }}>
            <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Part Details:
            </Text>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              <div><strong>Part Number:</strong> {partToSave.part_number}</div>
              <div><strong>Description:</strong> {partToSave.description}</div>
              <div><strong>Unit Price:</strong> {formatCurrency(partToSave.unit_price)}</div>
              <div><strong>Availability:</strong> {partToSave.availability}</div>
              {vehicle && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                  <div><strong>Vehicle:</strong> {vehicle.year} {vehicle.make} {vehicle.model}</div>
                  {vehicle.vin && <div><strong>VIN:</strong> {vehicle.vin}</div>}
                </div>
              )}
            </div>
          </div>
        )}
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
          Discounts on this part will be saved with the template.
        </Text>
        <Form form={savePartTemplateForm} layout="vertical">
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: 'Please enter a template name' },
              { min: 1, message: 'Template name must not be empty' }
            ]}
          >
            <Input placeholder="Enter a name for this template" autoFocus />
          </Form.Item>
          <Form.Item
            name="isGeneral"
            valuePropName="checked"
            help={!vehicle ? undefined : "If unchecked, this template will only appear when working with similar vehicles"}
          >
            <Checkbox>
              Make this a general template (available for all vehicles)
            </Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isGeneral !== currentValues.isGeneral}
          >
            {({ getFieldValue }) =>
              !getFieldValue('isGeneral') && vehicle ? (
                <Form.Item
                  name="vehicleYears"
                  label="Vehicle Years"
                  rules={[{ required: true, message: 'Please select at least one year' }]}
                  help="Select all years this part fits"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select years"
                    showSearch
                    filterOption={(input, option) => {
                      const yearValue = option?.value;
                      if (typeof yearValue === 'number') {
                        return yearValue.toString().includes(input);
                      }
                      return false;
                    }}
                  >
                    {yearOptions.map(year => (
                      <Select.Option key={year} value={year}>
                        {year}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
  );

  if (stackCards) {
    return (
      <div style={{ backgroundColor: '#f8fafc', padding: '12px 16px', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <Text strong style={{ color: '#374151', fontSize: '13px', fontWeight: 600 }}>
            Parts ({(lineItem.parts || []).length})
          </Text>
          <Space size="small">
            <Button type="default" size="small" icon={<StarOutlined />} onClick={() => setSavedPartTemplateModalVisible(true)} disabled={disabled} style={{ color: '#f59e0b', borderColor: '#f59e0b' }}>Saved</Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addNewPart} disabled={disabled} style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}>Add</Button>
          </Space>
        </div>
        <Form form={partForm} component={false}>
          {(lineItem.parts || []).length === 0 && (
            <div style={{ padding: '12px', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>No parts</Text>
            </div>
          )}
          {(lineItem.parts || []).map((part) => {
            const pKey = String(part.id ?? part.part_number ?? 'new-part');
            if (isEditingPart(part)) {
              // Use the shared edit-form function so the stacked path and the
              // desktop table (Part # column colSpan) are always in sync.
              return (
                <React.Fragment key={pKey}>
                  {renderPartEditFields({
                    partDiscountType, setPartDiscountType, partForm,
                    canEditPrices, cancelPartEdit, savePartEdit,
                    partKey: String(part.id ?? 'new'),
                  })}
                </React.Fragment>
              );
            }
            // Non-editing part row
            const baseTotal = part.unit_price * part.quantity;
            const discountedTotal = part.discounted_price ?? (baseTotal - (part.discount_amount || 0));
            const partCost = partUnitCost(part) * (part.quantity || 0);
            return (
              <div key={pKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                  <div style={{ fontWeight: 500, fontSize: 12, wordBreak: 'break-word' }}>
                    {part.description || '—'}
                    {Boolean(part.is_ai_suggested) && (
                      <Tag color="geekblue" style={{ marginLeft: 4, fontSize: 9, padding: '0 3px', lineHeight: '16px', verticalAlign: 'middle' }}>AI ✦</Tag>
                    )}
                  </div>
                  {part.part_number && (
                    <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{part.part_number}</Text>
                  )}
                  {part.supplier && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{part.supplier}</Text>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {discountStack(baseTotal, discountedTotal, { explicitPct: part.discount_percentage || undefined })}
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{part.quantity} × {formatCurrency(part.unit_price)}</div>
                  {canSeeCost && (
                    <div style={{ marginTop: 2 }}><MarginPopover revenue={discountedTotal} cost={partCost} /></div>
                  )}
                </div>
                {/* Overflow menu is a sibling of the two content columns, NOT stacked
                    inside the price column. Stacked, the 44px touch target sat below
                    the price block and pushed the kebab to the bottom edge of the row,
                    where it read as belonging to the next part. */}
                {!disabled && (
                  <div style={{ flexShrink: 0, marginLeft: 4 }}>
                    <Dropdown
                      menu={{
                        items: [
                          { key: 'edit', icon: <EditOutlined />, label: 'Edit' },
                          { key: 'save-template', icon: <SaveOutlined />, label: 'Save as template' },
                          { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
                        ],
                        onClick: ({ key }) => {
                          if (key === 'edit') editPart(part);
                          else if (key === 'save-template') savePartAsTemplate(part);
                          else if (key === 'delete') { setPartToDelete(String(part.id ?? 'new')); setDeletePartModalVisible(true); }
                        },
                      }}
                      trigger={['click']}
                    >
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        aria-label={`Actions for ${part.description || 'part'}`}
                        style={{ width: 44, height: 44, minWidth: 44, padding: 0 }}
                      />
                    </Dropdown>
                  </div>
                )}
              </div>
            );
          })}
        </Form>
        <TemplatePicker open={savedPartTemplateModalVisible} onClose={() => setSavedPartTemplateModalVisible(false)} onApply={(t) => addFromSavedPartTemplate(t)} />
        {partSaveModal}
        <Modal title="Delete Part" open={deletePartModalVisible} onOk={handleConfirmDeletePart} onCancel={() => { setDeletePartModalVisible(false); setPartToDelete(''); }} okText="Delete" okType="danger" cancelText="Cancel" centered>
          <p>Are you sure you want to delete this part? This action cannot be undone.</p>
        </Modal>
      </div>
    );
  }

  // ─── Desktop table layout (≥ 1024 px) ─────────────────────────────────────
  return (
    <div style={{ backgroundColor: '#f8fafc', padding: '12px 16px', width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <Text strong style={{ color: '#374151', fontSize: '13px', fontWeight: 600 }}>Parts ({(lineItem.parts || []).length})</Text>
        </div>
        <Space size="small">
          <Button
            type="default"
            size="small"
            icon={<StarOutlined />}
            onClick={() => setSavedPartTemplateModalVisible(true)}
            disabled={disabled}
            style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
          >
            Saved
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={addNewPart}
            disabled={disabled}
            style={{
              backgroundColor: '#10b981',
              borderColor: '#10b981',
            }}
          >
            Add
          </Button>
        </Space>
      </div>
      <Form form={partForm} component={false}>
        <Table
          columns={partColumns}
          dataSource={lineItem.parts || []}
          rowKey={record => String(record.id ?? record.part_number ?? record.description ?? 'new-part')}
          pagination={false}
          size="small"
          scroll={{ x: undefined }}
          locale={{
            emptyText: (
              <div style={{ padding: '12px', textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>No parts</Text>
              </div>
            ),
          }}
        />
      </Form>

      <TemplatePicker open={savedPartTemplateModalVisible} onClose={() => setSavedPartTemplateModalVisible(false)} onApply={(t) => addFromSavedPartTemplate(t)} />
      {partSaveModal}
      <Modal
        title="Delete Part"
        open={deletePartModalVisible}
        onOk={handleConfirmDeletePart}
        onCancel={() => {
          setDeletePartModalVisible(false);
          setPartToDelete('');
        }}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        centered
      >
        <p>Are you sure you want to delete this part? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

const ServiceItemsTable: React.FC<ServiceItemsTableProps> = ({
  items,
  onChange,
  disabled = false,
  taxRate = 7,
  vehicle,
}) => {
  const canEditPrices = useCapability('edit_prices');
  // Owners hold every capability, so this is "owner OR the flag" (RoleGuard).
  const canSetCustomLaborRate = useCapability('custom_labor_rate');
  const [editingKey, setEditingKey] = useState<string>('');
  const [savedTemplateModalVisible, setSavedTemplateModalVisible] = useState(false);
  const [deleteItemModalVisible, setDeleteItemModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string>('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Cards below BREAKPOINTS.xl (1280px). The dense table needs ~900px, but on the
  // estimate form it competes with the app nav AND the Customer & Vehicle rail, so
  // below 1280 there isn't room — use the card list (fits any width) and reserve the
  // table for >=1280 where the content column is wide enough.
  const stackCards = useBreakpointDown('xl');
  const canSeeLineMargin = useIsOwner();
  const [discountType, setDiscountType] = useState<'percentage' | 'amount' | 'final_price'>('percentage');
  const [defaultLaborRate, setDefaultLaborRate] = useState<number>(175);
  const [saveLineItemTemplateModalVisible, setSaveLineItemTemplateModalVisible] = useState(false);
  const [lineItemToSave, setLineItemToSave] = useState<EstimateLineItem | null>(null);
  // Track which rows have their Parts panel expanded
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  // Track in-progress hours edits for 'low' confidence pending items: itemId → draft hours value
  const [lowConfHoursDraft, setLowConfHoursDraft] = useState<Record<string, number | null>>({});
  // Track save-to-library checkbox state per item: itemId → boolean (default true)
  const [saveToLibraryChecked, setSaveToLibraryChecked] = useState<Record<string, boolean>>({});
  const [partsMarkup] = useState(40);
  // C2-T18: line item photos modal
  const [photoModalItemId, setPhotoModalItemId] = useState<number | null>(null);
  const itemsRef = React.useRef(items);
  React.useEffect(() => { itemsRef.current = items; }, [items]);
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const [form] = Form.useForm();
  const [saveLineItemTemplateForm] = Form.useForm();
  // Lifted from PartsSection so part-edit state survives a resize crossing 1024px (I2a).
  const [partForm] = Form.useForm();
  const [editingPartKey, setEditingPartKey] = useState<string>('');
  const [partDiscountType, setPartDiscountType] = useState<'percentage' | 'amount' | 'final_price'>('percentage');
  const [laborRates, setLaborRates] = useState<{ id: number; hourly_rate: number }[]>([]);
  const [markupPresets, setMarkupPresets] = useState<{ id: number; name: string; markup_percentage: number; is_default: number }[]>([]);
  const [rateDiffPending, setRateDiffPending] = useState<{ line: EstimateLineItem; savedRate: number; currentRate: number } | null>(null);

  // Load shop settings to get default labor rate
  useEffect(() => {
    const loadShopSettings = async () => {
      try {
        const response = await shopSettingsApi.get();
        setDefaultLaborRate(response.data.labor_rate);
      } catch (error) {
        console.error('Error loading shop settings:', error);
        // Keep default of 175 if API fails
      }
    };
    loadShopSettings();
  }, []);

  // Load labor rates once for template apply rate-diff resolution
  useEffect(() => {
    laborRateApi.getAll().then(r => setLaborRates(r.data)).catch(() => setLaborRates([]));
  }, []);

  // Load markup presets once for part editor
  useEffect(() => {
    partMarkupApi.getAll().then(r => setMarkupPresets(r.data)).catch(() => setMarkupPresets([]));
  }, []);

  // Auto-expand Parts panel for any pending AI item that has parts
  const prevPendingKeysRef = useRef<string>('');
  useEffect(() => {
    const pendingWithParts = items
      .filter(i => i.approval_status === 'pending' && (i.parts || []).length > 0)
      .map(i => String(i.id ?? 'new'));
    const key = pendingWithParts.join(',');
    if (key && key !== prevPendingKeysRef.current) {
      prevPendingKeysRef.current = key;
      setExpandedRowKeys(prev => {
        const combined = Array.from(new Set([...prev, ...pendingWithParts]));
        return combined;
      });
    }
  }, [items]);

  // Real-time calculation function for labor costs
  const recalculateLineTotalOnChange = () => {
    if (editingKey) {
      const formValues = form.getFieldsValue();
      const currentRecord = items.find(item => (item.id || 'new').toString() === editingKey);

      if (currentRecord && formValues) {
        const baseLaborCost = (formValues.labor_hours || 0) * (formValues.hourly_rate || 0);
        const { discountAmount: laborDiscountAmount, net: discountedLaborCost } = computeDiscount({
          base: baseLaborCost,
          mode: formValues.labor_discount_final_price > 0 ? 'final_price'
              : formValues.labor_discount_amount ? 'amount' : 'percentage',
          percentage: formValues.labor_discount_percentage,
          amount: formValues.labor_discount_amount,
          finalPrice: formValues.labor_discount_final_price,
        });
        const partsCost = (currentRecord.parts || []).reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);
        const newTotal = discountedLaborCost + partsCost;

        // Update the items array temporarily for display purposes
        const updatedItems = items.map(item => {
          if ((item.id || 'new').toString() === editingKey) {
            return {
              ...item,
              ...formValues,
              total_price: newTotal,
              labor_discount_amount: laborDiscountAmount,
            };
          }
          return item;
        });
        onChange(updatedItems);
      }
    }
  };

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsMobile(window.innerWidth < 768), 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  const isEditing = (record: EstimateLineItem) => (record.id || 'new').toString() === editingKey;

  const edit = (record: EstimateLineItem) => {
    form.setFieldsValue({
      category: record.category,
      description: record.description,
      quantity: record.quantity,
      labor_hours: record.labor_hours,
      hourly_rate: record.hourly_rate,
      labor_rate_id: record.labor_rate_id ?? null,
      labor_discount_percentage: record.labor_discount_percentage,
      labor_discount_amount: record.labor_discount_amount,
      notes: record.notes,
      is_diagnosis: !!record.is_diagnosis,
    });
    setEditingKey((record.id || 'new').toString());
  };

  const cancel = () => {
    // If the line item being edited has empty description, it's a new unsaved item - delete it
    const editingItem = items.find(item => (item.id || 'new').toString() === editingKey);

    if (editingItem && !editingItem.description) {
      // This is a new line item that was never saved - delete it
      const filteredItems = items.filter(item => (item.id || 'new').toString() !== editingKey);
      onChange(filteredItems);
    }
    // Otherwise it's an existing line item - just cancel the edit (revert happens automatically)

    setEditingKey('');
    form.resetFields();
  };

  const save = async (id: number | string) => {
    try {
      const values = await form.validateFields();
      const updatedItems = [...items];
      const index = updatedItems.findIndex(item => (item.id || 'new').toString() === id.toString());

      if (index > -1) {
        const item = updatedItems[index];
        const baseLaborCost = (values.labor_hours || 0) * (values.hourly_rate || 0);

        // Calculate labor discount
        const { discountAmount: laborDiscountAmount, net: discountedLaborCost } = computeDiscount({
          base: baseLaborCost,
          mode: values.labor_discount_final_price > 0 ? 'final_price'
              : values.labor_discount_amount ? 'amount' : 'percentage',
          percentage: values.labor_discount_percentage,
          amount: values.labor_discount_amount,
          finalPrice: values.labor_discount_final_price,
        });

        const partsCost = (item.parts || []).reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);

        // For services: combine discounted labor + parts cost
        const totalPrice = discountedLaborCost + partsCost;

        updatedItems[index] = {
          ...item,
          ...values,
          total_price: totalPrice,
          labor_hours: values.labor_hours || 0,
          hourly_rate: values.hourly_rate || 0,
          labor_discount_percentage: values.labor_discount_percentage || 0,
          labor_discount_amount: laborDiscountAmount,
          // Saving a pending AI item implicitly approves it
          approval_status: item.approval_status === 'pending' ? 'approved' : item.approval_status,
        };
      }

      onChange(updatedItems);
      setEditingKey('');
      form.resetFields();
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const addNewItem = () => {
    // The id and the rate come from the SAME source, so the picker never shows
    // one rate while the line charges another (see LaborRates/newLineRate.ts).
    const defaultRate = defaultNamedRate(laborRates);
    const newItem: EstimateLineItem = {
      id: Date.now(), // Temporary ID
      category: 'Custom',
      description: '',
      quantity: 1,
      labor_hours: 0,
      hourly_rate: seedHourlyRate(laborRates, defaultLaborRate),
      labor_rate_id: defaultRate?.id ?? null,
      labor_discount_percentage: 0,
      labor_discount_amount: 0,
      total_price: 0,
      parts: [],
    };
    onChange([...items, newItem]);
    edit(newItem);
  };

  const addFromSavedTemplate = (template: any /* Template */) => {
    const newItem = templateToLine(template, defaultLaborRate, laborRates, markupPresets);
    const rr = resolveLineRate((template.payload as any) || {}, laborRates);
    onChange([...items, newItem]);
    templateApi.use(template.id).catch(() => {});
    setSavedTemplateModalVisible(false);
    if (rr.diff && rr.savedRate != null && rr.currentRate != null) {
      setRateDiffPending({ line: newItem, savedRate: rr.savedRate, currentRate: rr.currentRate });
    } else {
      message.success(`Added ${template.name} to estimate`);
    }
  };

  const saveLineItemAsTemplate = (lineItem: EstimateLineItem) => {
    // Validate required fields before showing modal
    if (!lineItem.category || lineItem.category.trim() === '') {
      message.error('Cannot save template: Category is required');
      return;
    }
    if (!lineItem.description || lineItem.description.trim() === '') {
      message.error('Cannot save template: Description is required');
      return;
    }
    if (!lineItem.quantity || lineItem.quantity <= 0) {
      message.error('Cannot save template: Quantity must be greater than 0');
      return;
    }
    if (!Number.isInteger(lineItem.quantity)) {
      message.error('Cannot save template: Quantity must be a whole number');
      return;
    }

    setLineItemToSave(lineItem);
    saveLineItemTemplateForm.setFieldsValue({
      name: `${lineItem.description} - Service Template`,
      isGeneral: !vehicle, // Default to general if no vehicle, vehicle-specific if vehicle exists
      vehicleYears: vehicle ? [vehicle.year] : []
    });
    setSaveLineItemTemplateModalVisible(true);
  };

  // Generate year options from 1990 to current year + 2
  const serviceItemsYearOptions = Array.from({ length: new Date().getFullYear() + 2 - 1990 + 1 }, (_, i) => 1990 + i);

  const handleSaveLineItemTemplate = async () => {
    try {
      if (!lineItemToSave) return;

      const values = await saveLineItemTemplateForm.validateFields();

      // Build template object with only defined optional fields
      const template: Omit<LineItemTemplate, 'id'> = {
        name: values.name.trim(),
        category: lineItemToSave.category.trim(),
        description: lineItemToSave.description.trim(),
        quantity: lineItemToSave.quantity,
      };

      // Add optional fields only if they have valid values
      if (lineItemToSave.labor_hours !== undefined && lineItemToSave.labor_hours !== null) {
        template.labor_hours = lineItemToSave.labor_hours;
      }
      if (lineItemToSave.hourly_rate !== undefined && lineItemToSave.hourly_rate !== null) {
        template.hourly_rate = lineItemToSave.hourly_rate;
      }
      if (lineItemToSave.labor_discount_percentage !== undefined && lineItemToSave.labor_discount_percentage !== null) {
        template.labor_discount_percentage = lineItemToSave.labor_discount_percentage;
      }
      if (lineItemToSave.labor_discount_amount !== undefined && lineItemToSave.labor_discount_amount !== null) {
        template.labor_discount_amount = lineItemToSave.labor_discount_amount;
      }
      if (lineItemToSave.notes !== undefined && lineItemToSave.notes !== null && lineItemToSave.notes.trim() !== '') {
        template.notes = lineItemToSave.notes.trim();
      }

      // Add vehicle information if not marked as general
      if (!values.isGeneral && vehicle && values.vehicleYears && values.vehicleYears.length > 0) {
        template.vehicle_years = values.vehicleYears.join(',');
        template.vehicle_make = vehicle.make;
        template.vehicle_model = vehicle.model;
      }

      await templateApi.create(buildLineTemplate({ name: values.name, line: lineItemToSave, vehicle, vehicleYears: values.isGeneral ? undefined : values.vehicleYears }));
      message.success(`Line item template "${values.name}" saved successfully!`);
      setSaveLineItemTemplateModalVisible(false);
      setLineItemToSave(null);
      saveLineItemTemplateForm.resetFields();
    } catch (error) {
      console.error('Error saving line item template:', error);
      message.error('Failed to save line item template');
    }
  };

  const deleteItem = (id: number | string) => {
    console.log('Deleting line item with id:', id);
    console.log('Current items:', items);
    const filteredItems = items.filter(item => (item.id || 'new').toString() !== id.toString());
    console.log('Filtered items:', filteredItems);
    onChange(filteredItems);
  };

  /** Approve a single pending AI-suggested item (clears approval_status). */
  const approveItem = (id: number | string) => {
    const approvedItem = items.find(item => (item.id || 'new').toString() === id.toString());
    const updatedItems = items.map(item =>
      (item.id || 'new').toString() === id.toString()
        ? { ...item, approval_status: 'approved' as const }
        : item
    );
    onChange(updatedItems);

    if (approvedItem) {
      logEvent({
        event_id: newEventId(),
        event_type: 'estimate.service_item.approved',
        entity_type: 'estimate',
        source: 'ai_analyze',
        service_category: approvedItem.category,
        service_description: approvedItem.description?.substring(0, 200),
      });
    }
  };

  /** Approve all pending items at once.
   * - 'low' confidence items are SKIPPED (require manual hour entry).
   * - 'researched' confidence items are APPROVED but show an informational toast.
   * - 'verified' / 'estimated' items are approved silently.
   */
  const approveAllPending = () => {
    const lowConfCount = items.filter(
      item => item.approval_status === 'pending' && item.labor_confidence === 'low'
    ).length;
    const researchedCount = items.filter(
      item => item.approval_status === 'pending' && item.labor_confidence === 'researched'
    ).length;

    // Approve verified + estimated + researched; skip low
    const updatedItems = items.map(item =>
      item.approval_status === 'pending' && item.labor_confidence !== 'low'
        ? { ...item, approval_status: 'approved' as const }
        : item
    );
    onChange(updatedItems);

    if (lowConfCount > 0 && researchedCount > 0) {
      message.warning(
        `Approved items including ${researchedCount} AI-researched time${researchedCount > 1 ? 's' : ''}. ` +
        `${lowConfCount} item${lowConfCount > 1 ? 's' : ''} with unverified labor hours require manual confirmation. ` +
        `Visit Admin → Labor Times → AI Researched to verify researched entries.`
      );
    } else if (lowConfCount > 0) {
      message.warning(
        `Approved items with verified/estimated labor times. ${lowConfCount} item${lowConfCount > 1 ? 's' : ''} with unverified labor hours require manual confirmation.`
      );
    } else if (researchedCount > 0) {
      message.info(
        `All items approved. ${researchedCount} item${researchedCount > 1 ? 's' : ''} used AI-researched labor times. ` +
        `Visit Admin → Labor Times → AI Researched to verify.`
      );
    } else {
      message.success('All AI-suggested items approved');
    }
  };

  /** Save confirmed labor hours to the labor library for this vehicle context. */
  const saveLaborToLibrary = async (item: EstimateLineItem, confirmedHours: number): Promise<boolean> => {
    if (!vehicle?.make || !vehicle?.model || !vehicle?.year) return false;

    try {
      const { getTokens } = await import('../services/authApi');
      const tokens = getTokens();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (tokens?.accessToken) {
        headers['Authorization'] = `Bearer ${tokens.accessToken}`;
      }

      const body = {
        category: (item.category || 'other').toLowerCase().replace(/\s+/g, '_'),
        service_description: item.description,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        vehicle_year_from: vehicle.year,
        vehicle_year_to: vehicle.year,
        engine_size: vehicle.engine_size || null,
        transmission_type: null,
        flat_rate_hours: confirmedHours,
        notes: 'Advisor-confirmed from estimate',
      };

      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const resp = await fetch(`${apiUrl}/admin/labor-times`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (resp.status === 409) return false; // duplicate — caller handles
      return resp.ok;
    } catch {
      return false;
    }
  };

  /** Delete all pending items at once. */
  const deleteAllPending = () => {
    const filteredItems = items.filter(item => item.approval_status !== 'pending');
    onChange(filteredItems);
  };

  const handleConfirmDeleteItem = () => {
    console.log('User confirmed delete');
    deleteItem(itemToDelete);
    setDeleteItemModalVisible(false);
    setItemToDelete('');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'labor': return <ToolOutlined style={{ color: '#3b82f6' }} />;
      case 'part': return <ShoppingOutlined style={{ color: '#10b981' }} />;
      case 'service': return <SettingOutlined style={{ color: '#f59e0b' }} />;
      default: return <SettingOutlined />;
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Category',
      dataIndex: 'category',
      width: isMobile ? 75 : 110,
      render: (category: string, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        return (
          <Tag color="blue" style={{ fontSize: '10px' }}>
            {category}
          </Tag>
        );
      },
    },
    {
      title: 'Service Description',
      dataIndex: 'description',
      width: isMobile ? 130 : 200,
      render: (description: string, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        const progressText = record.notes;
        return (
          <div style={{ fontWeight: '500', fontSize: '13px', wordWrap: 'break-word', lineHeight: '1.3' }}>
            {description}
            {record.approval_status === 'pending' && (
              <Tag
                icon={<RobotOutlined />}
                color="blue"
                style={{ marginLeft: '6px', fontSize: '10px', verticalAlign: 'middle' }}
              >
                AI
              </Tag>
            )}
            {progressText && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', fontWeight: 'normal' }}>
                {progressText}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: isMobile ? 40 : 45,
      render: (quantity: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        return quantity;
      },
    },
    {
      title: 'Labor Hours',
      dataIndex: 'labor_hours',
      width: isMobile ? 65 : 100,
      render: (hours: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        const hoursDisplay = hours ? `${hours}h` : '—';

        // Show labor confidence badge only on pending AI-suggested items
        if (record.approval_status === 'pending' && record.labor_confidence) {
          const conf = record.labor_confidence;
          let badgeColor: string;
          let badgeLabel: string;
          let tooltipText: string;

          if (conf === 'verified') {
            badgeColor = '#10b981'; // green
            badgeLabel = '✓ verified';
            tooltipText = 'Labor time verified against your shop\'s reference database. High confidence.';
          } else if (conf === 'researched') {
            badgeColor = '#3b82f6'; // blue
            badgeLabel = '🔍 AI researched';
            const researchNotes = (record as any).labor_research_notes;
            tooltipText = researchNotes
              ? `AI-researched labor time (web + LLM). Source: ${researchNotes}. ` +
                'Review in Admin → Labor Times → AI Researched before promoting to library.'
              : 'Labor time sourced via AI web research (Perplexity/Groq). ' +
                'Review in Admin → Labor Times → AI Researched before promoting to library.';
          } else if (conf === 'low') {
            badgeColor = '#ef4444'; // red
            badgeLabel = '⚠ verify time';
            tooltipText =
              'AI has limited data for this specific job + vehicle combination. ' +
              'Enter the correct hours and confirm before approving.';
          } else {
            // 'estimated'
            badgeColor = '#f59e0b'; // amber
            badgeLabel = '~ estimated';
            tooltipText =
              'AI estimated this time from cross-referenced flat-rate sources (ALLDATA, Mitchell1, service manuals). ' +
              'Sources are in reasonable agreement for this job + vehicle. Review before approving.';
          }

          // For 'low' confidence items: show inline hours input + save-to-library toggle
          if (conf === 'low') {
            const itemKey = String(record.id ?? record.description ?? 'low');
            const draftHours = lowConfHoursDraft[itemKey] ?? hours ?? null;
            const saveChecked = saveToLibraryChecked[itemKey] !== false; // default true

            const vehicleLabel = vehicle
              ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.engine_size ? ` ${vehicle.engine_size}` : ''}`
              : null;

            const handleConfirm = async () => {
              const finalHours = draftHours ?? hours;
              if (!finalHours || finalHours <= 0) {
                message.error('Enter valid labor hours before confirming.');
                return;
              }

              // Update the item with confirmed hours and mark as approved
              const updatedItems = items.map(i => {
                if ((i.id ?? i.description) === (record.id ?? record.description)) {
                  const baseLaborCost = finalHours * (i.hourly_rate || 0);
                  const discAmt = i.labor_discount_amount || (baseLaborCost * (i.labor_discount_percentage || 0) / 100);
                  const partsCost = (i.parts || []).reduce((s, p) => s + (p.discounted_price ?? p.total_price), 0);
                  return {
                    ...i,
                    labor_hours: finalHours,
                    labor_confidence: 'verified' as const,
                    approval_status: 'approved' as const,
                    total_price: (baseLaborCost - discAmt) + partsCost,
                  };
                }
                return i;
              });
              onChange(updatedItems);

              // Save to library if checkbox checked
              if (saveChecked && vehicle?.make && vehicle?.model) {
                const saved = await saveLaborToLibrary(record, finalHours);
                if (saved === false && saveChecked) {
                  // Try fetch result — if 409 it means duplicate
                  const { getTokens } = await import('../services/authApi');
                  const tokens = getTokens();
                  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                  if (tokens?.accessToken) headers['Authorization'] = `Bearer ${tokens.accessToken}`;
                  const apiUrl = process.env.REACT_APP_API_URL ?? '';
                  const checkResp = await fetch(`${apiUrl}/admin/labor-times`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      category: (record.category || 'other').toLowerCase().replace(/\s+/g, '_'),
                      service_description: record.description,
                      vehicle_make: vehicle?.make,
                      vehicle_model: vehicle?.model,
                      vehicle_year_from: vehicle?.year,
                      vehicle_year_to: vehicle?.year,
                      engine_size: vehicle?.engine_size || null,
                      transmission_type: null,
                      flat_rate_hours: finalHours,
                      notes: 'Advisor-confirmed from estimate',
                    }),
                  });
                  if (checkResp.status === 409) {
                    message.info('This job already exists in the library. Hours updated on this estimate only.');
                  } else if (checkResp.ok) {
                    message.success(`Saved ${finalHours}h for ${record.description} to library.`);
                  } else {
                    message.warning('Hours confirmed. Library save failed — try again from the Labor Times admin page.');
                  }
                } else if (saved) {
                  message.success(`Saved ${finalHours}h for ${record.description} to library.`);
                }
              } else {
                message.success(`Labor hours confirmed: ${finalHours}h`);
              }

              // Cleanup draft state
              setLowConfHoursDraft(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
              setSaveToLibraryChecked(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
            };

            return (
              <div style={{ lineHeight: 1.4 }}>
                <Tooltip title={tooltipText} placement="right">
                  <span style={{ fontSize: '10px', color: badgeColor, fontWeight: 600, cursor: 'help', display: 'block' }}>
                    {badgeLabel}
                  </span>
                </Tooltip>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <InputNumber
                    size="small"
                    min={0.1}
                    max={40}
                    step={0.1}
                    precision={1}
                    value={draftHours ?? undefined}
                    onChange={val => setLowConfHoursDraft(prev => ({ ...prev, [itemKey]: val ?? null }))}
                    style={{ width: 64 }}
                    placeholder="hrs"
                  />
                  <Tooltip title="Confirm hours and approve item">
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleConfirm}
                      disabled={disabled}
                      style={{ padding: '0 6px' }}
                    />
                  </Tooltip>
                </div>
                {vehicleLabel && (
                  <div style={{ marginTop: 4 }}>
                    <Checkbox
                      checked={saveChecked}
                      onChange={e => setSaveToLibraryChecked(prev => ({ ...prev, [itemKey]: e.target.checked }))}
                      style={{ fontSize: '10px' }}
                    >
                      <span style={{ fontSize: '10px', color: '#6b7280' }}>
                        Save {draftHours ? `${draftHours}h` : ''} for {vehicleLabel} to library
                      </span>
                    </Checkbox>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontWeight: 500 }}>{hoursDisplay}</div>
              <Tooltip title={tooltipText} placement="right">
                <span
                  style={{
                    fontSize: '10px',
                    color: badgeColor,
                    cursor: 'help',
                    whiteSpace: 'nowrap',
                    fontWeight: 400,
                  }}
                >
                  {badgeLabel}
                </span>
              </Tooltip>
              {record.procedure_steps && record.procedure_steps.length > 0 && (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    fontSize: '11px',
                    color: '#6366f1',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '10px',
                    padding: '1px 6px',
                    marginTop: '2px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowKeys((prev: string[]) =>
                      prev.includes(String(record.id))
                        ? prev.filter((k) => k !== String(record.id))
                        : [...prev, String(record.id)]
                    );
                  }}
                  title="Click to view procedure steps"
                >
                  {record.procedure_steps.length} steps
                </div>
              )}
            </div>
          );
        }

        return (
          <div>
            <span>{hoursDisplay}</span>
            {record.procedure_steps && record.procedure_steps.length > 0 && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '11px',
                  color: '#6366f1',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '10px',
                  padding: '1px 6px',
                  marginTop: '2px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedRowKeys((prev: string[]) =>
                    prev.includes(String(record.id))
                      ? prev.filter((k) => k !== String(record.id))
                      : [...prev, String(record.id)]
                  );
                }}
                title="Click to view procedure steps"
              >
                {record.procedure_steps.length} steps
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Rate',
      dataIndex: 'hourly_rate',
      width: isMobile ? 60 : 80,
      render: (rate: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }
        return rate ? `$${rate}/hr` : '—';
      },
    },
    {
      title: 'Labor Disc',
      dataIndex: 'labor_discount',
      width: 95,
      render: (_: any, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing
        }

        const hasDiscount = record.labor_discount_percentage || record.labor_discount_amount;
        if (!hasDiscount) return '—';

        return (
          <div style={{ fontSize: '11px' }}>
            {record.labor_discount_percentage && (
              <div style={{ color: '#ef4444' }}>{record.labor_discount_percentage.toFixed(1)}%</div>
            )}
            {Boolean(record.labor_discount_amount) && (
              <div style={{ color: '#ef4444' }}>-{formatCurrency(record.labor_discount_amount).substring(1)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      width: isMobile ? 75 : 95,
      render: (total: number, record: EstimateLineItem) => {
        if (isEditing(record)) {
          return null; // Custom row handles editing (shows total in the edit form)
        }

        const baseLaborCost = (record.labor_hours || 0) * (record.hourly_rate || 0);
        const laborDiscountAmount = record.labor_discount_amount ||
          (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
        const discountedLaborCost = baseLaborCost - laborDiscountAmount;
        const partsCost = (record.parts || []).reduce((sum, part) => sum + (part.discounted_price ?? part.total_price), 0);

        if (baseLaborCost > 0 && partsCost > 0) {
          return (
            <div>
              <Text strong style={{ color: '#10b981' }}>
                {formatCurrency(total)}
              </Text>
              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.2' }}>
                <div>
                  Labor: {laborDiscountAmount > 0 ? (
                    <>
                      <span style={{ textDecoration: 'line-through' }}>{formatCurrency(baseLaborCost)}</span>
                      <span style={{ color: '#10b981', marginLeft: '4px' }}>{formatCurrency(discountedLaborCost)}</span>
                    </>
                  ) : (
                    formatCurrency(baseLaborCost)
                  )}
                </div>
                <div>Parts: {formatCurrency(partsCost)}</div>
              </div>
            </div>
          );
        }

        if (baseLaborCost > 0) {
          return (
            <div>
              <Text strong style={{ color: '#10b981' }}>
                {formatCurrency(total)}
              </Text>
              {laborDiscountAmount > 0 && (
                <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.2' }}>
                  <span style={{ textDecoration: 'line-through' }}>{formatCurrency(baseLaborCost)}</span>
                  <span style={{ color: '#10b981', marginLeft: '4px' }}>{formatCurrency(discountedLaborCost)}</span>
                </div>
              )}
            </div>
          );
        }

        return (
          <Text strong style={{ color: '#10b981' }}>
            {formatCurrency(total)}
          </Text>
        );
      },
    },
    {
      title: 'Actions',
      width: isMobile ? 75 : 90,
      fixed: isMobile ? undefined : ('right' as const),
      render: (text: any, record: EstimateLineItem) => {
        const editable = isEditing(record);
        const recordKey = (record.id || 'new').toString();

        // Read-only view (e.g. estimate detail): no per-line mutation controls
        // (approve/edit/delete or the actions menu). Editing happens on the form.
        if (disabled) {
          return null;
        }

        if (editable) {
          return null; // Custom row handles editing (shows Save/Cancel buttons)
        }

        // Pending AI-suggested items get approve/edit/delete buttons instead of the menu
        if (record.approval_status === 'pending') {
          return (
            <Space size={2}>
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                title="Approve — add to estimate"
                onClick={() => approveItem(recordKey)}
                style={{ color: '#10b981', padding: '0 4px' }}
                disabled={disabled}
              />
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                title="Edit this service item"
                onClick={() => edit(record)}
                style={{ color: '#1677ff', padding: '0 4px' }}
                disabled={disabled}
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                title="Remove this AI suggestion"
                onClick={() => deleteItem(recordKey)}
                style={{ color: '#ef4444', padding: '0 4px' }}
                disabled={disabled}
              />
            </Space>
          );
        }

        const handleMenuClick = (e: { key: string }) => {
          switch (e.key) {
            case 'edit':
              edit(record);
              break;
            case 'save-template':
              saveLineItemAsTemplate(record);
              break;
            case 'delete':
              cancel();
              setItemToDelete(recordKey);
              setDeleteItemModalVisible(true);
              break;
          }
        };

        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
          },
          {
            key: 'save-template',
            icon: <SaveOutlined />,
            label: 'Save as Template',
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
          },
        ];

        return (
          <Space size={2}>
            {record.id && (
              <Button
                type="text"
                size="small"
                icon={<CameraOutlined />}
                title="View / add finding photos"
                onClick={() => setPhotoModalItemId(record.id!)}
                disabled={disabled}
                style={{ padding: '0 4px' }}
              />
            )}
            <Dropdown
              menu={{ items: menuItems, onClick: handleMenuClick }}
              trigger={['click']}
              disabled={disabled}
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                disabled={disabled}
              />
            </Dropdown>
          </Space>
        );
      },
    },
    Table.EXPAND_COLUMN,
  ], [isMobile, editingKey, items, disabled, vehicle, lowConfHoursDraft, saveToLibraryChecked, expandedRowKeys, discountType, defaultLaborRate]);

  const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;
  const pendingCount = items.filter(i => i.approval_status === 'pending').length;

  // ─── Context snapshot (used by renderItemCard for the card-mode edit form) ──
  const editRowCtxValue: EditRowContextValue = {
    items, editingKey, form, isMobile, narrow: stackCards, disabled, discountType, setDiscountType,
    defaultLaborRate, canEditPrices, canSetCustomLaborRate, save, cancel, recalculateLineTotalOnChange, columns,
  };

  // ─── Card renderer for narrow viewports (stackCards = true) ─────────────────
  // Each line item becomes a full-width <Card>. Column render() functions are
  // reused directly so all display logic (AI badges, labor/parts discount,
  // procedure-step link, approval actions) comes along for free.
  const renderItemCard = (record: EstimateLineItem) => {
    const itemKey = String(record.id ?? 'new');
    const partsExpanded = expandedRowKeys.includes(itemKey);

    if (isEditing(record)) {
      // Editing: reuse the same form content CustomRow uses, wrapped in a Card.
      return (
        <Card key={itemKey} data-testid="line-item-card" size="small" styles={{ body: { padding: 0 } }}>
          {renderLineItemEditContent(record, editRowCtxValue)}
        </Card>
      );
    }

    // Helpers to look up a column's render function by dataIndex or key.
    const colRender = (dataIndexOrKey: string, value: any) => {
      const col: any = columns.find((c: any) => c.dataIndex === dataIndexOrKey || c.key === dataIndexOrKey);
      return col?.render ? col.render(value, record, 0) : value;
    };

    // Actions column has no dataIndex, so find it by title.
    const actionsCol: any = columns.find((c: any) => c.title === 'Actions');
    const actionsNode = actionsCol?.render ? actionsCol.render(null, record, 0) : null;

    // Money breakdown (labor + parts + line total) for the readable card body.
    const baseLaborCost = (record.labor_hours || 0) * (record.hourly_rate || 0);
    const laborDiscountAmt = record.labor_discount_amount ||
      (baseLaborCost * (record.labor_discount_percentage || 0) / 100);
    const discountedLaborCost = baseLaborCost - laborDiscountAmt;
    const partsList = record.parts || [];
    const partsBase = partsList.reduce((s, p) => s + (p.unit_price || 0) * (p.quantity || 0), 0);
    const partsRevenue = partsList.reduce(
      (s, p) => s + (p.discounted_price ?? ((p.unit_price || 0) * (p.quantity || 0) - (p.discount_amount || 0))), 0);
    const partsCost = partsList.reduce((s, p) => s + partUnitCost(p) * (p.quantity || 0), 0);

    // Row with the label on the left and the shared discount stack on the right.
    const breakdownRow = (label: React.ReactNode, node: React.ReactNode) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '4px 0' }}>
        <Text style={{ fontSize: 12, color: '#6b7280' }}>{label}</Text>
        <div>{node}</div>
      </div>
    );

    return (
      <Card
        key={itemKey}
        data-testid="line-item-card"
        size="small"
        styles={{ body: { padding: '10px 12px' } }}
        style={{
          borderRadius: 8,
          borderColor: record.approval_status === 'pending' ? '#93c5fd' : undefined,
          backgroundColor: record.approval_status === 'pending' ? '#f0f7ff' : undefined,
        }}
      >
        {/* Header row: category + description + actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
            <div style={{ marginBottom: 4 }}>{colRender('category', record.category)}</div>
            <div>{colRender('description', record.description)}</div>
          </div>
          <div style={{ flexShrink: 0 }}>{actionsNode}</div>
        </div>

        {/* Compact meta line — labels stay next to their values */}
        <div style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 8px' }}>
          Qty {record.quantity} · {record.labor_hours || 0}h @ {formatCurrency(record.hourly_rate || 0)}/hr
        </div>

        {/* Money breakdown: labor + parts (combined) + line total — same stack as parts */}
        <div style={{ background: '#f9fafb', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 10px' }}>
          {breakdownRow('Labor', discountStack(baseLaborCost, discountedLaborCost, { explicitPct: record.labor_discount_percentage || undefined }))}
          {partsList.length > 0 && breakdownRow(`Parts (${partsList.length})`, discountStack(partsBase, partsRevenue))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 6 }}>
            <Text style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Total</Text>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>{formatCurrency(record.total_price)}</span>
          </div>
          {canSeeLineMargin && (
            <div style={{ textAlign: 'right', marginTop: 4 }}>
              <MarginPopover revenue={record.total_price} cost={partsCost} />
            </div>
          )}
        </div>

        {/* Parts toggle + PartsSection — hidden in read-only view (the totals
            block already summarises parts; part editing happens on the form). */}
        {!disabled && (
        <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
          <Button
            type={partsExpanded ? 'default' : 'primary'}
            size="small"
            icon={partsExpanded ? <MinusOutlined /> : <PlusOutlined />}
            onClick={() => setExpandedRowKeys(prev =>
              partsExpanded ? prev.filter(k => k !== itemKey) : [...prev, itemKey]
            )}
            style={{
              backgroundColor: partsExpanded ? '#f0f0f0' : '#1890ff',
              borderColor: partsExpanded ? '#d9d9d9' : '#1890ff',
              color: partsExpanded ? '#666' : '#fff',
              marginBottom: partsExpanded ? 8 : 0,
            }}
          >
            {partsExpanded ? 'Close' : 'Parts'}
          </Button>
          {partsExpanded && (
            <>
              <PartsSection
                lineItem={record}
                onChange={(updatedLineItem) => {
                  const updatedItems = items.map(item =>
                    item.id === record.id ? updatedLineItem : item
                  );
                  onChange(updatedItems);
                }}
                disabled={disabled}
                vehicle={vehicle}
                stackCards={true}
                partForm={partForm}
                editingPartKey={editingPartKey}
                setEditingPartKey={setEditingPartKey}
                partDiscountType={partDiscountType}
                setPartDiscountType={setPartDiscountType}
                markupPresets={markupPresets}
              />
              {((record.procedure_steps?.length ?? 0) > 0 ||
                (record.parts_researched?.length ?? 0) > 0 ||
                (record.special_tools_researched?.length ?? 0) > 0) && (
                <LineItemProcedureEditor
                  item={record}
                  itemIndex={items.findIndex((i) => i === record)}
                  onLaborHoursChange={(idx, newTotal) => {
                    const updated = items.map((it, i) =>
                      i === idx ? { ...it, labor_hours: newTotal } : it
                    );
                    onChange(updated);
                  }}
                  readOnly={disabled}
                />
              )}
            </>
          )}
        </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8, rowGap: 10, width: '100%', paddingTop: 6, paddingBottom: 6 }}>
            <Space size={8} style={{ flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>Service Items</span>
              {pendingCount > 0 && (
                <Tag icon={<RobotOutlined />} color="blue" style={{ fontWeight: 400, marginInlineEnd: 0 }}>
                  {pendingCount} AI suggestion{pendingCount !== 1 ? 's' : ''}
                </Tag>
              )}
            </Space>
            {!disabled && (
              <Space size={8} wrap style={{ rowGap: 8 }}>
                {pendingCount > 0 && (
                  <>
                    <Button
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={approveAllPending}
                      style={{ color: '#10b981', borderColor: '#10b981' }}
                    >
                      Approve All
                    </Button>
                    <Button
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={deleteAllPending}
                      danger
                    >
                      Delete Pending
                    </Button>
                  </>
                )}
                <Button
                  size="small"
                  type="default"
                  icon={<StarOutlined />}
                  onClick={() => setSavedTemplateModalVisible(true)}
                  style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
                >
                  My Templates
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addNewItem}
                >
                  Add Custom Item
                </Button>
              </Space>
            )}
          </div>
        }
        style={{ marginBottom: '24px' }}
        headStyle={{
          position: 'sticky',
          top: '-24px',
          zIndex: 10,
          backgroundColor: '#fff',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <style>{`
          .service-item-row td {
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            vertical-align: top !important;
            min-height: auto;
          }
          .service-item-row .ant-table-cell {
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            vertical-align: top !important;
            white-space: normal !important;
            word-wrap: break-word !important;
          }
          .ai-pending-row td {
            background-color: #f0f7ff !important;
          }
          .ai-pending-row:hover td {
            background-color: #e6f0fd !important;
          }
          .editing-row td {
            border-bottom: 2px solid #3b82f6 !important;
          }
          .editing-row .ant-form-item-label {
            padding-bottom: 4px !important;
          }
          .editing-row .ant-form-item-label > label {
            height: auto !important;
          }
        `}</style>
        <EditRowContext.Provider value={editRowCtxValue}>
        {stackCards ? (
          /* ── Card list (< 1024px) — no h-scrolling table ── */
          <Form form={form} component={false}>
            {items.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Text type="secondary">No items added yet</Text>
                {!disabled && (
                  <>
                    <br />
                    <Button type="link" icon={<PlusOutlined />} onClick={addNewItem}>
                      Add your first item
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.map((record) => renderItemCard(record))}
              </div>
            )}
          </Form>
        ) : (
          /* ── Desktop table (≥ 1024px) — unchanged except scroll.x dropped ── */
          <Form form={form} component={false}>
            <Table
              columns={columns}
              dataSource={items}
              rowKey={record => String(record.id ?? record.description ?? 'new')}
              pagination={false}
              size="small"
              components={{
                body: {
                  row: CustomRow,
                },
              }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                expandedRowRender: (record) => {
                  return (
                    <div>
                      <PartsSection
                        lineItem={record}
                        onChange={(updatedLineItem) => {
                          const updatedItems = items.map(item =>
                            item.id === record.id ? updatedLineItem : item
                          );
                          onChange(updatedItems);
                        }}
                        disabled={disabled}
                        vehicle={vehicle}
                        partForm={partForm}
                        editingPartKey={editingPartKey}
                        setEditingPartKey={setEditingPartKey}
                        partDiscountType={partDiscountType}
                        setPartDiscountType={setPartDiscountType}
                        markupPresets={markupPresets}
                      />
                      {/* The shop's own briefing for this line: tools, the parts
                          list with a reason per part, and the whole procedure
                          with its diagrams. Never shown to the customer — it is
                          stripped from every public payload. */}
                      <ShopNotesPanel
                        value={(record as any).shop_notes}
                        // What is already on this line, so a suggestion the
                        // advisor has taken shows as added rather than offering
                        // to add it twice.
                        partsOnLine={(record.parts ?? []).map((p: any) => ({
                          part_number: p.part_number ?? null,
                          description: p.description ?? '',
                        }))}
                        // Nothing reaches the ticket on its own — these only
                        // run when the advisor presses Add or Remove.
                        onAddPart={(suggested) =>
                          onChange(addSuggestedPart(items, record, suggested))
                        }
                        onRemovePart={(_suggested, onLine) =>
                          onChange(removeSuggestedPart(items, record, onLine))
                        }
                      />
                      {((record.procedure_steps?.length ?? 0) > 0 ||
                        (record.parts_researched?.length ?? 0) > 0 ||
                        (record.special_tools_researched?.length ?? 0) > 0) && (
                        <LineItemProcedureEditor
                          item={record}
                          itemIndex={items.findIndex((i) => i === record)}
                          onLaborHoursChange={(idx, newTotal) => {
                            const updated = items.map((it, i) =>
                              i === idx ? { ...it, labor_hours: newTotal } : it
                            );
                            onChange(updated);
                          }}
                          readOnly={disabled}
                        />
                      )}
                    </div>
                  );
                },
                expandIcon: ({ expanded, onExpand, record }) => (
                  <Button
                    type={expanded ? 'default' : 'primary'}
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={(e) => onExpand(record, e)}
                    style={{
                      backgroundColor: expanded ? '#f0f0f0' : '#1890ff',
                      borderColor: expanded ? '#d9d9d9' : '#1890ff',
                      color: expanded ? '#666' : '#fff',
                    }}
                  >
                    {expanded ? 'Close' : 'Parts'}
                  </Button>
                ),
                rowExpandable: () => true,
              }}
              locale={{
                emptyText: (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Text type="secondary">No items added yet</Text>
                    <br />
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={addNewItem}
                      disabled={disabled}
                    >
                      Add your first item
                    </Button>
                  </div>
                ),
              }}
              rowClassName={(record) =>
                record.approval_status === 'pending'
                  ? 'service-item-row ai-pending-row'
                  : 'service-item-row'
              }
            />
          </Form>
        )}
        </EditRowContext.Provider>

      </Card>

      <TemplatePicker open={savedTemplateModalVisible} onClose={() => setSavedTemplateModalVisible(false)} onApply={(t) => addFromSavedTemplate(t)} />
      {rateDiffPending && (
        <RateDiffPrompt
          open={true}
          savedRate={rateDiffPending.savedRate}
          currentRate={rateDiffPending.currentRate}
          onUpdate={() => {
            message.success('Added with current rate');
            setRateDiffPending(null);
          }}
          onKeep={() => {
            const snapshotRate = rateDiffPending.savedRate;
            const baseLaborCost = (rateDiffPending.line.labor_hours || 0) * snapshotRate;
            const laborDiscount = rateDiffPending.line.labor_discount_amount || (baseLaborCost * (rateDiffPending.line.labor_discount_percentage || 0) / 100);
            const partsCost = (rateDiffPending.line.parts || []).reduce((s: number, p: any) => s + (p.discounted_price ?? p.total_price), 0);
            const updatedLine: EstimateLineItem = {
              ...rateDiffPending.line,
              hourly_rate: snapshotRate,
              total_price: (baseLaborCost - laborDiscount) + partsCost,
            };
            onChange(items.map(it => it.id === updatedLine.id ? updatedLine : it));
            message.success('Added with saved rate');
            setRateDiffPending(null);
          }}
        />
      )}
      <Modal
        title="Save Line Item as Template"
        open={saveLineItemTemplateModalVisible}
        onOk={handleSaveLineItemTemplate}
        onCancel={() => {
          setSaveLineItemTemplateModalVisible(false);
          setLineItemToSave(null);
          saveLineItemTemplateForm.resetFields();
        }}
        okText="Save Template"
        cancelText="Cancel"
        centered
        width={550}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Save this service item as a reusable template. You can use it later in any estimate.
          </Text>
        </div>
        {lineItemToSave && (
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '1px solid #e5e7eb'
          }}>
            <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Service Item Details:
            </Text>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              <div><strong>Category:</strong> {lineItemToSave.category}</div>
              <div><strong>Description:</strong> {lineItemToSave.description}</div>
              <div><strong>Quantity:</strong> {lineItemToSave.quantity}</div>
              {lineItemToSave.labor_hours && lineItemToSave.labor_hours > 0 && (
                <div><strong>Labor:</strong> {lineItemToSave.labor_hours}h @ ${lineItemToSave.hourly_rate}/hr</div>
              )}
              <div><strong>Total Price:</strong> {formatCurrency(lineItemToSave.total_price)}</div>
            </div>
          </div>
        )}
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
          Discounts on this line will be saved with the template.
        </Text>
        <Form form={saveLineItemTemplateForm} layout="vertical">
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: 'Please enter a template name' },
              { min: 1, message: 'Template name must not be empty' }
            ]}
          >
            <Input placeholder="Enter a name for this template" autoFocus />
          </Form.Item>
          <Form.Item
            name="isGeneral"
            valuePropName="checked"
            help={!vehicle ? undefined : "If unchecked, this template will only appear when working with similar vehicles"}
          >
            <Checkbox>
              Make this a general template (available for all vehicles)
            </Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isGeneral !== currentValues.isGeneral}
          >
            {({ getFieldValue }) =>
              !getFieldValue('isGeneral') && vehicle ? (
                <Form.Item
                  name="vehicleYears"
                  label="Vehicle Years"
                  rules={[{ required: true, message: 'Please select at least one year' }]}
                  help="Select all years this service applies to"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select years"
                    showSearch
                    filterOption={(input, option) => {
                      const yearValue = option?.value;
                      if (typeof yearValue === 'number') {
                        return yearValue.toString().includes(input);
                      }
                      return false;
                    }}
                  >
                    {serviceItemsYearOptions.map(year => (
                      <Select.Option key={year} value={year}>
                        {year}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Delete Line Item"
        open={deleteItemModalVisible}
        onOk={handleConfirmDeleteItem}
        onCancel={() => {
          setDeleteItemModalVisible(false);
          setItemToDelete('');
        }}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        centered
      >
        <p>Are you sure you want to delete this line item? This action cannot be undone.</p>
      </Modal>

      {/* C2-T18: Line item photos modal */}
      <Modal
        title="Line Item Photos"
        open={photoModalItemId !== null}
        onCancel={() => setPhotoModalItemId(null)}
        footer={null}
        centered
        width={480}
      >
        {photoModalItemId !== null && (
          <LineItemPhotosControl lineItemId={photoModalItemId} phase="finding" />
        )}
      </Modal>

    </div>
  );
};

export default ServiceItemsTable;
